import { t } from "i18next"
import { AsyncState, HttpResult, loadingAsyncState, unloadedAsyncState } from "widgets-for-react"
import { ProductFormState, FormInput, ProductSaleDonation, ProductSale, ProductPostageCosts, ProductApiResponseParsed, ProductForm } from "./productFormBlock.state"
import { ZipCode } from "../../../utils/ZipcodeChecker"
import { convertPathToUrl } from "../donationFormBlock/utils"

export const getPostcodeLoader = (s: ProductFormState): AsyncState<ZipCode.CheckResponse> => {
  const zipCode = s.input.postalCode.replace(" ", "");
  const houseNumber = Number.parseInt(s.input.houseNumber)
  if (!isNaN(houseNumber) && zipCode.length == 6) return loadingAsyncState(() => ZipCode.CheckRequest({ postcode: zipCode, houseNumber: houseNumber }))
  return unloadedAsyncState()
}

const getAmountForForm = (form: ProductForm) => {
  if (form.kind === "product_sale") return form.price
  if (form.kind === "product_package_costs") {
    return form.sponsor ? form.price : 0
  }

  if (form.kind === "product_sale_donation") {
    if (!form.wantToDonate) return form.price
    if (form.selectedIndex === undefined) return form.price + form.manualAmount!

    return form.price + form.amounts[form.selectedIndex].amount
  }
}

const getSelectedAmount = (form: ProductForm) => {
  if (form.kind !== "product_sale_donation") return "N.V.T"
  if (!form.wantToDonate) return "N.V.T"
  if (form.selectedIndex === undefined) return "anders"
  return form.amounts[form.selectedIndex].amount
}

export const submitForm: (state: ProductFormState, recaptcha: string) => Promise<HttpResult<{}>> =
  async (state, recaptcha) => {
    if (state.donationFormLoader.kind !== "loaded") return { kind: "failed", status: 400 }
    const form = state.donationFormLoader.value

    try {
      const endpoint = `/api/v1/payments/donate`
      let headers = new Headers()
      headers.append('content-type', 'application/json')
      headers.append('X-XSRF-TOKEN', (document.getElementsByName("__RequestVerificationToken")[0] as any).value)

      const totalAmount = getAmountForForm(form.productForm)
      const betaalMethode = totalAmount! <= 0 ? "None" : "iDeal"

      const input = {
        Email: state.input.email,
        Straat: state.input.street,
        Initalen: state.input.firstName,
        Geslacht: state.input.salutation,
        Tussenvoegsel: state.input.insertion,
        Huisnummer: state.input.houseNumber,
        Postcode: state.input.postalCode,
        Telefoon: state.input.phone,
        Nieuwsbrief: state.input.acceptNewsLetter,
        Achternaam: state.input.lastName,
        Plaats: state.input.city,
        Toevoeging: state.input.houseNumberAddition,
        IBAN: "",
      }

      const returnUrls =
      {
        SuccessUrl: convertPathToUrl(form.formInfo.successUrl),
        FailedUrl: convertPathToUrl(form.formInfo.failedUrl),
        CancelUrl: convertPathToUrl(form.formInfo.cancelUrl ),
      }

      const body = 
      {
        ...input,
        FormID: form.formInfo.drupalformId,
        ProductId: form.productId,
        FormKind: form.productForm.kind,
        Formulier: state.title,
        Betaalmethode: betaalMethode,
        Donatie : "Eenmalig",
        SelectAmount: getSelectedAmount(form.productForm),
        Bedrag: getAmountForForm(form.productForm),
        Voorwaarden: "", //?
        recaptcha,
        ...returnUrls,
      }

      let res = await fetch(endpoint, {
        method: 'post', credentials: 'include', headers: headers, body: JSON.stringify(body)
      })

      if (!res.ok) throw Error(res.statusText)
      const redirectURL = await res.text()
      window.location.href = redirectURL

      return {
        kind: 'result',
        status: 200,
        value: {}
      }
    } catch {
      return {
        kind: 'failed',
        status: 500
      }
    }
  }

export const donationFormRequest: (
  formId: number
) => Promise<HttpResult<ProductApiResponseParsed>> =
  async (
    formId
  ) => {
    try {
      const endpoint = `/api/drupal/donationForm?formId=${formId}`
      let headers = new Headers()
      headers.append('content-type', 'application/json')
      headers.append('X-XSRF-TOKEN', (document.getElementsByName("__RequestVerificationToken")[0] as any).value)

      let res = await fetch(endpoint, {
        method: 'get', credentials: 'include', headers: headers
      })

      if (!res.ok) throw Error(res.statusText)
      let json = await res.json()

      const value = parseProductForm(json)

      return {
        kind: 'result',
        status: 200,
        value: value
      }
    } catch (e) {

      console.error(`Error when fetching form`)
      console.error(e)

      return {
        kind: 'failed',
        status: 500
      }
    }
  }

const getSegmentForForm = (json: any): ProductForm => {
  const type = json["type"][0]["target_id"]
  if (type === "product_sale") {
    return parseProductSale(json)
  }
  if (type === "product_sale_donation") {
    return parseProductSaleDonation(json)
  }
  if (type === "postage_costs") {
    return parseProductPostageCosts(json)
  }
  throw "impossible"
}

const parseProductForm = (json: any): ProductApiResponseParsed => {
  return {
    productForm: getSegmentForForm(json["field_productsoort"][0]["target"]),
    submitButton: json["field_verzendknop"]?.[0]?.["value"],
    customTitle: json["field_preset_title_single_1"]?.[0]?.["value"],
    productId: json["field_product_id"][0]["value"],
    formInfo: {
      drupalformId: json["nid"][0]["value"],

      successUrl: json["field_page_succesful_payment"]?.[0]?.["url"], 
      failedUrl: json["field_page_failed_payment"]?.[0]?.["url"],
      cancelUrl: json["field_page_cancelled_payment"]?.[0]?.["url"],
    },
  }
}

const parseProductSaleDonation = (json: any): ProductSaleDonation => ({
  kind: "product_sale_donation",
  minimumAmount: Number(json["field_minimum_amount"][0]["value"]),
  maximumAmount: Number(json["field_maximum_amount"]?.[0]?.value || 5000),
  amounts: [1, 2, 3].map(input => {
    const donationOption = json[`field_donation_option_${input}`][0]["target"]
    return {
      amount: Number(donationOption["field_price"][0]["value"]),
      button: donationOption["field_label"]?.[0]?.["value"] || "",
    }
  }),
  price: Number(json["field_price"][0]["value"]),
  selectedIndex: 1,
})

const parseProductSale = (json: any): ProductSale => ({
  kind: "product_sale",
  price: Number(json["field_price"][0]["value"]),
})

const parseProductPostageCosts = (json: any): ProductPostageCosts => ({
  kind: "product_package_costs",
  price: Number(json["field_price"][0]["value"]),
  sponsor: undefined,
})

export const isFieldRequired = (field: keyof FormInput): boolean => {
  return ([
    'email',
    'firstName',
    'lastName',
    'postalCode',
    'houseNumber',
  ]).indexOf(field) > -1
}
